import {
  AnchoredPopup,
  Field,
  IconButton,
  SaveIcon,
  Spinner,
  Switch,
  TextInput,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import {
  useEffect,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  findSelfRecipient,
  getWorkspaceNotifications,
  isSelfNotificationOn,
  listMyWorkspaces,
  moveSelfRecipient,
  setSelfNotification,
  updateCurrentMeNotificationEmail,
  type TrainingNotificationView,
} from "@/api";
import { useAppState } from "@/app";
import { resolveLoadErrorMessage, type LoadState } from "@/components/dataset";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import styles from "./TrainingNotificationToggle.module.css";

// 학습이 끝나면 Slack 알림을 받을지 정하는 내 토글. Training 헤더에 있다.
//
// 팝업은 두 단이다. 위는 **알림을 받을 Slack 이메일**, 아래는 토글이다. 이메일은 로그인 계정의
// 이메일이 아니라 사용자가 직접 입력하는 값이다(`/v1/me` 의 `notification_email`). 로그인
// 이메일은 GitHub noreply 주소일 수 있어서 Slack 이 사용자를 찾지 못한다. 상류는 이메일로만
// Slack 사용자를 찾는다. 이메일이 없으면 수신자가 될 이메일이 없으므로 토글을 잠근다.
//
// 토글 하나가 두 가지를 함께 켠다:
//   - 그 이메일을 수신자로 등록한다. 그래야 알림이 나에게 온다.
//   - 이후 제출이 알림을 켠 상태로 나간다. 그래야 통지가 만들어진다.
// 둘 다 필요한 이유는 `api/trainingNotification` 의 머리 주석이 소유한다. 제출 payload 쪽 주입은
// 제출 위저드가 같은 경계를 거쳐 한다. 이 컴포넌트는 수신자 축만 바꾼다.
//
// 수신자 행의 원본은 상류다. 그래서 registry 에 저장하는 상태가 없다. 팝업을 열 때 읽고, 바꾸면
// 바로 상류에 쓴다. 이메일을 바꾸면 저장 뒤 이전 이메일의 내 행을 새 이메일로 옮긴다. 그래서
// 켜짐 상태와 `last_error` 이력이 이어진다.
//
// 워크스페이스는 화면 어휘로 노출하지 않는다(docs/rules.md §15). 내 스코프에서 도출하고,
// 문구는 "학습 팀"으로 쓴다.

const TOGGLE_LABEL = "Training이 끝나면 Slack 으로 받기";
const EMAIL_LABEL = "알림을 받을 Slack 이메일";
const NO_TEAM_MESSAGE = "Training 팀 매핑이 필요합니다. 관리자에게 요청해 주세요.";
const NO_EMAIL_MESSAGE = "알림을 받을 Slack 이메일을 먼저 저장해 주세요.";
const EMAIL_HELP_MESSAGE = "Slack 계정에 등록된 이메일과 같아야 알림이 전달됩니다.";
const SAVE_ERROR_MESSAGE = "알림 설정을 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.";
const EMAIL_SAVE_ERROR_MESSAGE = "이메일을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.";

export function TrainingNotificationToggle(): ReactNode {
  const popup = useAnchoredPopupAnchor();
  const { state: appState, applyCurrentUserProfile } = useAppState();
  const email = appState.currentUser?.notificationEmail ?? null;
  const displayName = appState.currentUser?.displayName ?? null;

  const [state, setState] = useState<LoadState<TrainingNotificationView[]>>({ status: "idle" });
  // 저장 중 화면에 표시할 값. 응답이 오면 상류 상태로 되돌린다(실패하면 롤백한다).
  const [optimisticOn, setOptimisticOn] = useState<boolean | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 이메일 입력은 저장 전까지 화면에만 있는 값이다. 저장된 값이 바뀌면(저장 성공·다른 탭) 그 값을 따른다.
  const [emailDraft, setEmailDraft] = useState(email ?? "");
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  useEffect(() => {
    setEmailDraft(email ?? "");
  }, [email]);

  // 내 학습 팀 스코프 → 그 안의 알림 상태. 팀은 보통 하나지만, 여럿이면 모두 읽어 함께 켠다.
  async function load(): Promise<void> {
    setState({ status: "loading" });
    try {
      const workspaces = await listMyWorkspaces();
      const views = await Promise.all(
        workspaces.map((workspace) => getWorkspaceNotifications(workspace.id)),
      );
      setState({ status: "success", data: views });
    } catch (error) {
      setState({ status: "error", message: resolveLoadErrorMessage(error) });
    }
  }

  // 자동 조회는 팝업을 여는 동작에서 한 번만 한다. 닫힌 헤더에서는 요청을 만들지 않는다. 조회 상태를
  // 보고 다시 읽는 effect 를 두면 실패할 때마다 다시 요청한다. 실패한 뒤에는 사용자가 `다시 시도` 로 읽는다.
  function handleOpen(event: ReactMouseEvent<HTMLElement>): void {
    popup.openFromElement(event);
    if (state.status !== "success" && state.status !== "loading") {
      void load();
    }
  }

  const views = state.status === "success" ? state.data : [];
  // 팀이 여럿이면 모두 켜져 있을 때만 켜진 것으로 본다. 일부만 켜진 상태를 "켜짐"으로 표시하면
  // 알림을 못 받는 학습이 생긴다.
  const savedOn =
    email !== null && views.length > 0 && views.every((view) => isSelfNotificationOn(view, email));
  const checked = optimisticOn ?? savedOn;
  const lastError =
    email === null
      ? null
      : (views
          .map((view) => findSelfRecipient(view, email)?.last_error ?? null)
          .find((message) => message !== null) ?? null);

  const canToggle = email !== null && state.status === "success" && views.length > 0;
  const trimmedDraft = emailDraft.trim();
  const canSaveEmail =
    !savingEmail && trimmedDraft !== "" && trimmedDraft.toLowerCase() !== (email ?? "");

  async function handleChange(next: boolean): Promise<void> {
    if (!canToggle || email === null || state.status !== "success") {
      return;
    }
    setOptimisticOn(next);
    setSaveError(null);
    try {
      const updated = await Promise.all(
        state.data.map((view) =>
          setSelfNotification(view.workspace_id, view, { email, displayName }, next),
        ),
      );
      setState({ status: "success", data: updated });
    } catch (error) {
      setSaveError(resolveSaveErrorMessage(error, SAVE_ERROR_MESSAGE));
      // 일부만 성공했을 수 있다. 그래서 화면 값을 추측하지 않고 상류를 다시 읽는다.
      await load();
    } finally {
      setOptimisticOn(null);
    }
  }

  // 이메일을 저장한 뒤 이전 이메일로 등록된 내 행을 새 이메일로 옮긴다. 저장이 먼저다. 행을 먼저
  // 옮기고 저장이 실패하면 토글이 이전 이메일 기준으로 남는다. 그러면 켜 둔 알림이 꺼진 것처럼 보인다.
  async function handleSaveEmail(): Promise<void> {
    if (!canSaveEmail) {
      return;
    }
    setSavingEmail(true);
    setEmailError(null);
    const previous = email;
    try {
      const profile = await updateCurrentMeNotificationEmail(trimmedDraft);
      applyCurrentUserProfile(profile);
      const saved = profile.user.notification_email;
      if (previous !== null && saved !== null && state.status === "success") {
        const moved = await Promise.all(
          state.data.map((view) => moveSelfRecipient(view.workspace_id, view, previous, saved)),
        );
        setState({ status: "success", data: moved });
      }
    } catch (error) {
      setEmailError(resolveSaveErrorMessage(error, EMAIL_SAVE_ERROR_MESSAGE));
      // 저장은 성공하고 옮기기만 실패했을 수 있다. 그래서 상류를 다시 읽는다.
      if (state.status === "success") {
        await load();
      }
    } finally {
      setSavingEmail(false);
    }
  }

  return (
    <span className={styles.anchor}>
      <IconButton
        aria-label="Training 알림"
        title="Training이 끝나면 Slack 으로 알림을 받을지 정합니다."
        variant="surface"
        selected={popup.open}
        aria-expanded={popup.open}
        aria-haspopup="dialog"
        onClick={handleOpen}
      >
        <BellIcon />
      </IconButton>
      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={popup.close}
        placement="bottom-end"
        role="dialog"
        aria-label="Training 알림"
        width="22rem"
      >
        <AnchoredPopup.Header divider>
          <span className={styles.title}>Training 알림</span>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md">
          {/* 검증 경계는 서버 한 곳이다. 앱이 문구와 위치를 정할 수 없으므로 브라우저 기본 이메일 말풍선은 끈다. */}
          <form
            className={styles.emailForm}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void handleSaveEmail();
            }}
          >
            <Field label={EMAIL_LABEL} layout="stack" disabled={savingEmail}>
              <div className={styles.emailRow}>
                <TextInput
                  type="email"
                  value={emailDraft}
                  maxLength={255}
                  placeholder="name@carbon6robotics.com"
                  autoComplete="email"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setEmailDraft(event.target.value)
                  }
                />
                {/* 아이콘 전용 버튼. 22rem 팝업에서 텍스트 버튼은 입력에 밀려 라벨이 두 줄이 된다.
                    접근성 이름은 "저장" 으로 둔다. */}
                <IconButton
                  aria-label={savingEmail ? "저장 중" : "저장"}
                  title="이메일 저장"
                  type="submit"
                  variant="solid"
                  disabled={!canSaveEmail}
                >
                  {savingEmail ? <Spinner size="md" /> : <SaveIcon />}
                </IconButton>
              </div>
            </Field>
            <p className={styles.notice}>{EMAIL_HELP_MESSAGE}</p>
            {emailError === null ? null : <p className={styles.error}>{emailError}</p>}
          </form>
          <div className={styles.divider} />
          <div className={styles.row}>
            <span className={styles.label}>{TOGGLE_LABEL}</span>
            <Switch
              checked={checked}
              disabled={!canToggle}
              aria-label={TOGGLE_LABEL}
              onChange={(event) => void handleChange(event.target.checked)}
            />
          </div>
          {email === null ? <p className={styles.notice}>{NO_EMAIL_MESSAGE}</p> : null}
          {state.status === "loading" ? (
            <div className={styles.stateSlot}>
              <LoadingState size="sm">알림 설정을 불러오는 중입니다…</LoadingState>
            </div>
          ) : null}
          {state.status === "error" ? (
            <div className={styles.stateSlot}>
              <EmptyState
                size="sm"
                tone="error"
                action={{ label: "다시 시도", onClick: () => void load() }}
              >
                {state.message}
              </EmptyState>
            </div>
          ) : null}
          {state.status === "success" && views.length === 0 ? (
            <div className={styles.stateSlot}>
              <EmptyState size="sm">{NO_TEAM_MESSAGE}</EmptyState>
            </div>
          ) : null}
          {canToggle ? (
            <p className={styles.notice}>
              {email} 으로 Slack 알림을 보냅니다. 켜 두면 이후 제출하는 Training에 적용되고, 같은
              Training 팀 구성원에게도 함께 갑니다.
            </p>
          ) : null}
          {lastError === null ? null : (
            <p className={styles.error}>최근 알림을 보내지 못했습니다: {lastError}</p>
          )}
          {saveError === null ? null : <p className={styles.error}>{saveError}</p>}
        </AnchoredPopup.Body>
      </AnchoredPopup>
    </span>
  );
}

// 저장 실패 문구. 서버가 사유를 주면 그 사유를 쓰고, 아니면 다시 시도하라고 안내한다.
function resolveSaveErrorMessage(error: unknown, fallback: string): string {
  const message = resolveLoadErrorMessage(error);
  return message === "" ? fallback : message;
}

// 알림 글리프(종). c6 는 도메인 아이콘에 런타임 의존하지 않으므로 앱에서 정의한다.
function BellIcon(): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 2a4 4 0 0 0-4 4v2.5L2.75 11h10.5L12 8.5V6a4 4 0 0 0-4-4Z" />
      <path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
    </svg>
  );
}
